import { UserId } from '@streem/domain-id';
import { contextValues } from '@streem/logger';
import { invariant, getPreferredVideoInputId, getPreferredAudioInputId, getPreferredAudioOutputId, } from '@streem/toolbox';
import { combineLatest, Subject, of } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { CameraSubject } from 'streem-sdk-protobuf';
import { logger } from '../../../util/logger';
import { createVideoTrackName, generateStreemVideoTrackConstraints, } from '../../../util/track_constraints';
import { accumulateAndMapWallEvents, findInGroup } from '../../wall/utils';
import { LocalAudioController, RemoteAudioController } from '../audio_controllers';
import { DataController } from '../data_controller';
import { MediaBuilder } from './media_builder';
import { VideoState } from './video_state';
import { filterNull } from '../../../util/rx/filter_null';
export class MediaController {
    constructor(config, observables, trackProvider) {
        this.config = config;
        this.destroyed = new Subject();
        invariant(this.config.context.localUserId, 'Expected localUserId to be defined when initializing MediaController');
        invariant(this.config.context.remoteUserId, 'Expected remoteUserId to be defined when initializing MediaController');
        invariant(this.config.context.callSettings, 'Expected callSettings to be defined when initializing MediaController');
        this.trackProvider = trackProvider;
        const trackProviderName = this.trackProvider.trackProviderName;
        logger.debug(`Your track provider is ${trackProviderName}`);
        logger.setContextValue(contextValues.TRACK_PROVIDER, trackProviderName);
        this.mediaBuilder = new MediaBuilder(this.trackProvider, this.config.roomWall);
        this.videoState = new VideoState({ ...config, trackProvider: this.trackProvider });
        this.videoControllers = this.videoState.values;
        this.localVideoController = this.videoState.localController;
        this.localAudio = new LocalAudioController({
            trackSource: this.trackProvider.localAudioTrack,
            userId: this.config.context.localUserId,
            roomId: this.config.context.roomId,
            roomWall: this.config.roomWall,
            wallItem: this.config.roomWall.itemsByType.pipe(findInGroup('audioTrack'), accumulateAndMapWallEvents('audioTrack'), map(audioTrackWallItems => {
                const [latestTrack] = audioTrackWallItems
                    .filter(item => item.ownerUserId === this.config.context.localUserId?.toString())
                    .reverse();
                return latestTrack;
            })),
        });
        this.remoteAudio = new RemoteAudioController({
            trackSource: this.trackProvider.remoteAudioTrack,
            userId: this.config.context.remoteUserId,
            roomId: this.config.context.roomId,
            roomWall: this.config.roomWall,
            wallItem: this.config.roomWall.itemsByType.pipe(findInGroup('audioTrack'), accumulateAndMapWallEvents('audioTrack'), map(audioTrackWallItems => {
                const [latestTrack] = audioTrackWallItems
                    .filter(item => item.ownerUserId === this.config.context.remoteUserId?.toString())
                    .reverse();
                return latestTrack;
            })),
        });
        this.data = new DataController(this.trackProvider.localDataTrack, this.trackProvider.remoteDataTrack, this.config.context.localUserId);
        this.remoteNetworkQuality = this.trackProvider.remoteNetworkQuality;
        this.localNetworkQuality = this.trackProvider.localNetworkQuality;
        this.getUserMediaVideoError = this.trackProvider.getUserMediaVideoError.pipe(filterNull());
        this.getUserMediaAudioError = this.trackProvider.getUserMediaAudioError.pipe(filterNull());
        this.roomWebRtcStats = this.trackProvider.mediaWebRtcStats.pipe(filterNull(), takeUntil(this.destroyed));
        if (this.trackProvider.isLocalVideoFrozen !== undefined) {
            this.isLocalVideoFrozen = this.trackProvider.isLocalVideoFrozen.pipe(filterNull(), takeUntil(this.destroyed));
        }
        const videoTracks = this.config.roomWall.itemsByType.pipe(findInGroup('videoTrack'), accumulateAndMapWallEvents('videoTrack'), takeUntil(this.destroyed));
        /**
         *
         * Create a VideoController for a VideoTrack WallItem
         * if it has not been created yet.
         *
         */
        videoTracks
            .pipe(takeUntil(this.destroyed))
            .subscribe((wallItems) => {
            let j = wallItems.length;
            while (j--) {
                this.videoState.createNewController({
                    wallItemId: wallItems[j].id,
                    ownerUserId: new UserId(wallItems[j].ownerUserId),
                    videoTrackName: wallItems[j].name ?? undefined,
                });
            }
        });
        this.remoteParticipantCapabilities = observables.remoteParticipantCapabilities;
        this.localVideoLabel = this.trackProvider.localVideoTrack.pipe(filterNull(), map(track => track.trackLabel), takeUntil(this.destroyed));
        this.localAudioLabel = this.trackProvider.localAudioTrack.pipe(filterNull(), map(track => track.trackLabel), takeUntil(this.destroyed));
        this.localVideoLabel.subscribe(label => {
            logger.info(`Call initiated with ${label} camera`);
        });
        this.localAudioLabel.subscribe(label => {
            logger.info(`Call initiated with ${label} microphone`);
        });
        if (this.trackProvider.localDevices !== undefined) {
            this.localDevices = this.trackProvider.localDevices.pipe(filterNull(), takeUntil(this.destroyed));
        }
        else {
            this.localDevices = of([]);
        }
        this.hasRemoteUserConnection = this.trackProvider.remoteConnection;
        this.connectionState = this.trackProvider.connectionState;
    }
    async selectMic({ deviceId, label, groupId, }) {
        await this.localAudio.restart({ deviceId, groupId });
        logger.info(`Expert changed microphone to: ${label}`);
    }
    async selectSpeaker({ deviceId, label }) {
        await this.remoteAudio.selectSpeaker(deviceId);
        logger.info(`Expert changed speaker to: ${label}`);
    }
    async getStats() {
        invariant(this.trackProvider.getStats, 'Attempted to get stats on a remote media track. Did you mean to call this on the local track instead?');
        return await this.trackProvider.getStats();
    }
    async connect(roomId, userId) {
        combineLatest([this.localAudio.connected, this.localAudio.enabled])
            .pipe(takeUntil(this.destroyed))
            .subscribe(([connected, enabled]) => {
            if (connected) {
                const item = this.config.roomWall.builder.buildTrackWallItem('audioTrack', {
                    active: enabled,
                });
                this.config.roomWall.addOrUpdate(item);
            }
            else {
                this.config.roomWall.delete('audioTrack');
            }
        });
        this.trackProvider.localDataTrack
            .pipe(takeUntil(this.destroyed))
            .subscribe(track => track
            ? this.config.roomWall.addOrUpdate(this.config.roomWall.builder.buildTrackWallItem('dataTrack', {}))
            : this.config.roomWall.delete('dataTrack'));
        await this.trackProvider.connect(roomId, userId);
    }
    async disconnect() {
        this.destroyed.next(undefined);
        this.videoState.clearState();
        this.data.disconnect();
        this.localAudio.disconnect();
        this.remoteAudio.disconnect();
        await this.trackProvider.disconnect();
    }
    async createSpectatorTracks() {
        invariant(this.config.context.callSettings); // this should be caught in construction
        const faceToFaceModeEnabled = this.config.context.callSettings.faceToFaceModeEnabled;
        const cameraSubject = CameraSubject.CAMERA_SUBJECT_PERSON;
        const preferredCameraId = getPreferredVideoInputId();
        const preferredMicId = getPreferredAudioInputId();
        const preferredAudioOutputDeviceId = getPreferredAudioOutputId();
        await this.mediaBuilder.createMedia({
            preferredAudioOutputDeviceId: preferredAudioOutputDeviceId ?? 'default',
            constraints: {
                audio: preferredMicId ? { deviceId: preferredMicId } : true,
                ...(faceToFaceModeEnabled && {
                    video: await generateStreemVideoTrackConstraints(cameraSubject, preferredCameraId),
                }),
            },
            video: {
                name: (() => {
                    if (faceToFaceModeEnabled && this.trackProvider.useNamedVideoTracks) {
                        return createVideoTrackName(this.config.context.localDeviceId, cameraSubject);
                    }
                    else {
                        return '';
                    }
                })(),
                cameraSubject: cameraSubject,
                active: true,
            },
        });
    }
    async createHostTracks() {
        invariant(this.config.context.callSettings); // this should be caught in construction
        const faceToFaceModeEnabled = this.config.context.callSettings.faceToFaceModeEnabled;
        const cameraSubject = faceToFaceModeEnabled
            ? CameraSubject.CAMERA_SUBJECT_PERSON
            : CameraSubject.CAMERA_SUBJECT_WORLD;
        await this.mediaBuilder.createMedia({
            constraints: {
                audio: true,
                video: await generateStreemVideoTrackConstraints(cameraSubject),
            },
            video: {
                name: (() => {
                    if (faceToFaceModeEnabled && this.trackProvider.useNamedVideoTracks) {
                        return createVideoTrackName(this.config.context.localDeviceId, cameraSubject);
                    }
                    else {
                        return '';
                    }
                })(),
                cameraSubject: cameraSubject,
                active: true,
            },
        });
    }
}
//# sourceMappingURL=media_controller.js.map